"use client";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import {
  loginCustomer,
  logoutCustomer,
  refreshCustomerSession,
  getCustomerMe,
  updateCustomerProfile,
  changeCustomerPassword,
  type CustomerProfile,
  type LoginOutcome,
  type UpdateProfileOutcome,
  type ChangePasswordOutcome,
  type UpdateProfilePayload,
  type ChangePasswordPayload,
} from "@/lib/customer-auth-api";
import { resendConfirmation as resendConfirmationApi } from "@/lib/account-api";

/**
 * Access token lives only in memory (component state) — never localStorage — per the
 * security requirement that a stolen XSS payload shouldn't be able to read a long-lived
 * token from storage. The refresh token is an HttpOnly cookie this code can't see at all.
 * On mount, a silent refresh() re-derives a fresh access token from that cookie so a page
 * reload doesn't drop the session; if there's no valid cookie, refresh() simply reports
 * "not authenticated" instead of erroring.
 */
interface CustomerAuthContextType {
  customer: CustomerProfile | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, languageCode: string, rememberMe?: boolean) => Promise<LoginOutcome>;
  /** Adopts an access token issued by a non-login endpoint that already establishes a
   * session server-side (Faz 4's quote-access "register" — the refresh cookie is set by
   * that same response) — fetches /me the same way `login` does, without re-deriving any
   * auth logic here. */
  adoptSession: (accessToken: string) => Promise<CustomerProfile | null>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  /** Re-fetches /me with the current access token and updates `customer` in place — used
   * after a profile edit so the Header (and any other consumer) re-renders immediately,
   * without a full page reload. */
  refreshMe: () => Promise<CustomerProfile | null>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<UpdateProfileOutcome>;
  changePassword: (payload: ChangePasswordPayload) => Promise<ChangePasswordOutcome>;
  resendConfirmation: (languageCode: string) => Promise<boolean>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const useCustomerAuthContext = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error("useCustomerAuthContext must be used within a CustomerAuthProvider");
  }
  return context;
};

export const CustomerAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current = accessToken;

  const refresh = useCallback(async (): Promise<boolean> => {
    const result = await refreshCustomerSession();
    if (!result.ok || result.data?.outcome !== "Success" || !result.data.accessToken) {
      setAccessToken(null);
      setCustomer(null);
      return false;
    }

    setAccessToken(result.data.accessToken);
    const me = await getCustomerMe(result.data.accessToken);
    setCustomer(me);
    return true;
  }, []);

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string, languageCode: string, rememberMe?: boolean): Promise<LoginOutcome> => {
      const result = await loginCustomer({ email, password, languageCode, rememberMe });
      const outcome = result.data?.outcome ?? "InvalidCredentials";

      if (outcome === "Success" && result.data?.accessToken) {
        setAccessToken(result.data.accessToken);
        const me = await getCustomerMe(result.data.accessToken);
        setCustomer(me);
      }

      return outcome;
    },
    [],
  );

  const adoptSession = useCallback(async (accessToken: string): Promise<CustomerProfile | null> => {
    setAccessToken(accessToken);
    const me = await getCustomerMe(accessToken);
    setCustomer(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    await logoutCustomer();
    setAccessToken(null);
    setCustomer(null);
  }, []);

  const refreshMe = useCallback(async (): Promise<CustomerProfile | null> => {
    if (!accessTokenRef.current) return null;
    const me = await getCustomerMe(accessTokenRef.current);
    setCustomer(me);
    return me;
  }, []);

  const updateProfile = useCallback(async (payload: UpdateProfilePayload): Promise<UpdateProfileOutcome> => {
    if (!accessTokenRef.current) return "AccountNotFound";
    const result = await updateCustomerProfile(accessTokenRef.current, payload);

    if (result.ok) {
      // Success response body IS the fresh CustomerProfile (see PublicAccountController.MapMe) —
      // no `outcome` wrapper on the 200 path.
      setCustomer(result.data as CustomerProfile);
      return "Success";
    }

    if (result.status === 401) return "AccountNotFound";
    const outcome = (result.data as { outcome?: UpdateProfileOutcome } | null)?.outcome;
    return outcome ?? "InvalidCountry";
  }, []);

  const changePassword = useCallback(async (payload: ChangePasswordPayload): Promise<ChangePasswordOutcome> => {
    if (!accessTokenRef.current) return "AccountNotFound";
    const result = await changeCustomerPassword(accessTokenRef.current, payload);
    const outcome = result.data?.outcome ?? "AccountNotFound";

    if (outcome === "Success") {
      // Server already revoked every refresh token and cleared its cookie on this
      // response — mirror that locally so no stale in-memory access token lingers.
      setAccessToken(null);
      setCustomer(null);
    }

    return outcome;
  }, []);

  const resendConfirmation = useCallback(async (languageCode: string): Promise<boolean> => {
    if (!customer?.email) return false;
    const result = await resendConfirmationApi(customer.email, languageCode);
    return result.ok;
  }, [customer?.email]);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        accessToken,
        isAuthenticated: Boolean(accessToken && customer),
        isLoading,
        login,
        adoptSession,
        logout,
        refresh,
        refreshMe,
        updateProfile,
        changePassword,
        resendConfirmation,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};
